import React, { useState } from 'react';
import { workExperienceData } from '../../data/portfolioData';

const WorkExperience = () => {
  const [activeJob, setActiveJob] = useState(workExperienceData[0].id);
  const [visibleDetails, setVisibleDetails] = useState(true);
  
  const handleJobClick = (id) => {
    if (activeJob === id) {
      setVisibleDetails(!visibleDetails);
    } else {
      setActiveJob(id);
      setVisibleDetails(true);
    }
  };
  
  const activeJobData = workExperienceData.find(job => job.id === activeJob);
  
  return (
    <div className="py-16 relative">
      {/* Section header */}
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">Work Experience</h2>
        <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
          My professional journey through various roles and organizations
        </p>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-5 gap-8">
        {/* Timeline navigation - left side */}
        <div className="md:col-span-2">
          <div className="sticky top-24">
            <div className="space-y-1">
              {workExperienceData.map((job) => (
                <button
                  key={job.id}
                  onClick={() => handleJobClick(job.id)}
                  className={`w-full text-left p-4 rounded-lg transition-all duration-300 ${
                    activeJob === job.id 
                      ? 'bg-gray-100 dark:bg-gray-800 border-l-4 border-blue-500' 
                      : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  }`}
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0 mr-4">
                      {/* Replace with actual logo or placeholder */}
                      <div className="w-10 h-10 rounded-md bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400">
                        {job.company.charAt(0)}
                      </div>
                    </div>
                    <div>
                      <h3 className={`font-medium ${activeJob === job.id ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-gray-200'}`}>
                        {job.role}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{job.company}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500">{job.duration}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Job details - right side */}
        <div className="md:col-span-3">
          <div className={`bg-white dark:bg-gray-800/50 rounded-xl p-6 border border-gray-200 dark:border-gray-700 transform transition-all duration-500 ${
            visibleDetails ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
          }`}>
            <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{activeJobData.role}</h3>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="text-gray-800 dark:text-gray-300">{activeJobData.company}</span>
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                <span className="text-gray-600 dark:text-gray-400">{activeJobData.location}</span>
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                <span className="text-gray-600 dark:text-gray-400">{activeJobData.duration}</span>
              </div>
              <p className="text-gray-700 dark:text-gray-300">{activeJobData.description}</p>
            </div>

            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Key Responsibilities</h4>
              <ul className="space-y-2">
                {activeJobData.responsibilities.map((responsibility, index) => (
                  <li key={index} className="flex items-start">
                    <svg className="w-5 h-5 text-blue-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                    <span className="text-gray-700 dark:text-gray-300">{responsibility}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Technologies & Skills</h4>
              <div className="flex flex-wrap gap-2">
                {activeJobData.technologies.map((tech, index) => (
                  <span key={index} className="px-3 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 rounded-full text-sm border border-blue-100 dark:border-blue-800/30">
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Key Achievements</h4>
              <ul className="space-y-2">
                {activeJobData.achievements.map((achievement, index) => (
                  <li key={index} className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700 dark:text-gray-300">{achievement}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkExperience;